const DEFAULT_POLLING_INTERVAL = 5000;

export const isAbortError = (e: unknown): boolean => e instanceof Error && e.name === 'AbortError';

const createAbortError = (): Error => {
  const error = new Error('Aborted');
  error.name = 'AbortError';

  return error;
};

const throwIfAborted = (signal?: AbortSignal): void => {
  if (signal?.aborted) throw createAbortError();
};

const abortableSleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError());

      return;
    }

    let timeout: ReturnType<typeof setTimeout>;

    const onAbort = () => {
      clearTimeout(timeout);
      reject(createAbortError());
    };

    timeout = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    signal?.addEventListener('abort', onAbort, { once: true });
  });

export type PollTaskOptions<R> = {
  fetch: () => Promise<R>;
  isCompleted: (result: R) => boolean;
  getErrors: (result: R) => string[];
  /**
   * Whether an uncompleted task is still working. Omit to poll until the task completes; provide it
   * to stop — resolving `null` — as soon as the task stops without completing.
   */
  isRunning?: (result: R) => boolean;
  /** Called with every result the task reports, including the completed one. */
  onProgress?: (result: R) => void | Promise<void>;
  intervalMs?: number;
  signal?: AbortSignal;
};

/**
 * Polls a mission planner task until it completes.
 *
 * Resolves the completed result, or `null` when `isRunning` reports the task stopped without
 * completing. Rejects with the task's own errors when the completed task carries any, and throws
 * an `AbortError` once the signal is aborted — including when a fetch lands after the abort.
 */
export const pollTask = async <R>({
  fetch,
  isCompleted,
  getErrors,
  isRunning,
  onProgress,
  intervalMs = DEFAULT_POLLING_INTERVAL,
  signal,
}: PollTaskOptions<R>): Promise<R | null> => {
  const poll = async (): Promise<R | null> => {
    throwIfAborted(signal);

    const result = await fetch();

    throwIfAborted(signal);

    if (isCompleted(result)) {
      const errors = getErrors(result);

      if (errors.length) {
        throw new Error(errors.join(' '));
      }

      await onProgress?.(result);
      throwIfAborted(signal);

      return result;
    }

    if (isRunning && !isRunning(result)) {
      return null;
    }

    await onProgress?.(result);

    await abortableSleep(intervalMs, signal);

    return poll();
  };

  return poll();
};
